import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import DescriptionIcon from '@mui/icons-material/Description';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { DOCUMENT } from '../constants/documentTheme';
import { partnerOxbloodBtnSx, partnerLinkSx } from './PartnerListChrome';
import { PARTNER_BOTTOM_NAV_HEIGHT } from '../utils/partnerWorkspace';

const QUICK_TYPES = [
  { id: 'restaurant', category: 'restaurant', icon: RestaurantIcon },
  { id: 'transfer', category: 'transport', icon: DirectionsCarIcon, transportation_type: 'transfer' },
  { id: 'activity', category: 'activity', icon: LocalActivityIcon },
  { id: 'yacht', category: 'activity', icon: DirectionsBoatIcon, nameHint: 'Yacht' },
  { id: 'car', category: 'transport', icon: DirectionsCarIcon, transportation_type: 'car' },
  // Library pick only — Appilix native camera is unreliable (same as Memories).
  { id: 'photo', category: 'other', icon: PhotoLibraryIcon, nameHint: 'Photo' },
  { id: 'note', category: 'other', icon: NoteAltIcon, nameHint: 'Note' },
  { id: 'document', category: 'other', icon: DescriptionIcon, nameHint: 'Document' },
  { id: 'custom', category: 'other', icon: MoreHorizIcon },
];

/**
 * Mobile-first quick add for partner trip ops.
 * `onPick` receives { category, name?, transportation_type? } to open the itinerary dialog.
 * `onRecommend` opens the send-for-approval flow with the same seed.
 * `onPhoto` receives uploaded image URL after camera/library capture.
 */
const PartnerQuickAdd = ({
  onPick,
  onRecommend,
  onOpenDocuments,
  onPhoto,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [seed, setSeed] = useState(null);
  const [uploading, setUploading] = useState(false);
  const photoInputRef = useRef(null);

  const items = useMemo(() => QUICK_TYPES.map((item) => ({
    ...item,
    label: t(`partner.opsHome.quickTypes.${item.id}`, item.id),
  })), [t]);

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setSeed(null);
  };

  const handleType = (item) => {
    if (item.id === 'document' && onOpenDocuments) {
      setOpen(false);
      onOpenDocuments();
      return;
    }
    if (item.id === 'photo' && onPhoto) {
      photoInputRef.current?.click();
      return;
    }
    setSeed(item);
  };

  const handlePhotoSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !onPhoto) return;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('image', file);
      const response = await api.post(
        '/uploads/image?preset=itinerary&folder=itinerary',
        fd,
        { usePartnerAuth: true }
      );
      const url = response.data?.url || response.data?.imageUrl;
      if (url) {
        close();
        await onPhoto(url);
      }
    } catch (err) {
      console.error('Partner quick photo upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const addNow = () => {
    if (!seed) return;
    onPick?.({
      category: seed.category,
      name: seed.nameHint || '',
      transportation_type: seed.transportation_type || '',
    });
    close();
  };

  const recommend = () => {
    if (!seed) return;
    onRecommend?.({
      category: seed.category,
      name: seed.nameHint || '',
      transportation_type: seed.transportation_type || '',
    });
    close();
  };

  return (
    <>
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        hidden
        data-testid="partner-quick-add-photo-input"
        onChange={handlePhotoSelected}
      />
      <Box
        component="button"
        type="button"
        aria-label={t('partner.opsHome.quickAdd')}
        data-testid="partner-quick-add-fab"
        onClick={handleOpen}
        disabled={disabled || uploading}
        sx={{
          position: 'fixed',
          right: 16,
          bottom: {
            xs: `calc(${PARTNER_BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px) + 16px)`,
            md: 24,
          },
          zIndex: 25,
          width: 56,
          height: 56,
          borderRadius: '50%',
          border: 0,
          bgcolor: DOCUMENT.oxblood,
          color: DOCUMENT.ivory,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: disabled || uploading ? 'default' : 'pointer',
          opacity: disabled || uploading ? 0.5 : 1,
          boxShadow: '0 8px 24px rgba(0,0,0,0.22)',
        }}
      >
        <AddIcon />
      </Box>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={close}
        PaperProps={{ sx: { borderRadius: '12px 12px 0 0', maxHeight: '85vh' } }}
      >
        <Box sx={{ p: 2.5, pb: `calc(24px + env(safe-area-inset-bottom, 0px))` }} data-testid="partner-quick-add-sheet">
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22, mb: 0.5 }}>
            {t('partner.opsHome.quickAddTitle')}
          </Typography>
          <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mb: 2 }}>
            {uploading ? t('common.saving') : t('partner.opsHome.quickAddHint')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            {items.map((item) => {
              const Icon = item.icon;
              const selected = seed?.id === item.id;
              return (
                <Button
                  key={item.id}
                  data-testid={`partner-quick-add-${item.id}`}
                  onClick={() => handleType(item)}
                  disabled={uploading}
                  sx={{
                    justifyContent: 'flex-start',
                    gap: 1,
                    borderRadius: 0,
                    border: `1px solid ${selected ? DOCUMENT.oxblood : DOCUMENT.ruleHard}`,
                    bgcolor: selected ? 'color-mix(in srgb, #8C2B22 8%, white)' : DOCUMENT.surface,
                    color: DOCUMENT.ink,
                    px: 1.5,
                    py: 1.5,
                    textTransform: 'none',
                    fontFamily: DOCUMENT.sans,
                    fontSize: 13,
                  }}
                >
                  <Icon sx={{ fontSize: 20, color: DOCUMENT.oxblood }} />
                  {item.label}
                </Button>
              );
            })}
          </Box>
          {seed && seed.id !== 'document' && seed.id !== 'photo' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2.5 }}>
              <Button onClick={addNow} sx={partnerOxbloodBtnSx} data-testid="partner-quick-add-confirm">
                {t('partner.opsHome.quickAddConfirm')}
              </Button>
              <Button onClick={recommend} sx={{ ...partnerLinkSx, alignSelf: 'center' }} data-testid="partner-quick-add-recommend">
                {t('partner.opsHome.recommend')}
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default PartnerQuickAdd;
